/**
 * =============================================================================
 * useWlFinancialKpis
 *
 * Hook do painel WL -> Financeiro (Visao Geral). Consome a RPC
 * `wl_get_financial_kpis` que devolve KPIs consolidados, agrupamentos por
 * plano/add-on e series de 12 meses.
 *
 * Cache com TanStack Query, staleTime 60s e refetch em foco.
 *
 * @version 1.0.0
 * RELEASE: 2026-07-21 v1.0.0 - Versao inicial.
 * =============================================================================
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/** Item KPI padrao (valor + delta%). */
export interface WlFinancialKpiValue {
  value: number;
  delta_pct?: number;
  count?: number;
}

/** Item de agrupamento por plano. */
export interface WlFinancialPlanRow {
  plan_id: string;
  plan_name: string;
  tenant_count: number;
  mrr: number;
}

/** Item de agrupamento por add-on. */
export interface WlFinancialAddonRow {
  addon_id: string;
  addon_name: string;
  addon_slug: string;
  quantity: number;
  mrr: number;
}

/** Serie mensal de receita. */
export interface WlFinancialRevenuePoint {
  month: string;
  mrr: number;
  /** MRR proveniente de planos no mes (v1.7.0+ da RPC). */
  mrr_plans?: number;
  /** MRR proveniente de add-ons no mes (v1.7.0+ da RPC). */
  mrr_addons?: number;
  new_mrr: number;
  churn_mrr: number;
}

/** Serie mensal de tenants. */
export interface WlFinancialTenantPoint {
  month: string;
  new_tenants: number;
  churned_tenants: number;
  active_total: number;
}

/** Payload completo devolvido pela RPC. */
export interface WlFinancialKpisPayload {
  reference_month: string;
  timezone: string;
  kpis: {
    mrr: WlFinancialKpiValue;
    arr: WlFinancialKpiValue;
    nmrr: WlFinancialKpiValue;
    revenue_churn: WlFinancialKpiValue;
    paying_tenants: WlFinancialKpiValue;
    avg_ticket: WlFinancialKpiValue;
    addons_total: WlFinancialKpiValue;
    revenue_at_risk: WlFinancialKpiValue;
  };
  by_plan: WlFinancialPlanRow[];
  by_addon: WlFinancialAddonRow[];
  trend_revenue: WlFinancialRevenuePoint[];
  trend_tenants: WlFinancialTenantPoint[];
}

/**
 * Hook principal do dashboard financeiro do WL.
 *
 * @param referenceMonth - Mes de referencia no formato ISO (yyyy-MM-dd).
 *                        Quando omitido, backend usa o mes corrente.
 * @param timezone       - Timezone das agregacoes (default America/Sao_Paulo).
 * @param statuses       - Lista opcional de status a considerar nos KPIs.
 *                        Ex.: ['active'] (default UI), ['active','past_due'].
 *                        Quando undefined/vazio, o backend usa
 *                        active + past_due (comportamento historico).
 */
export function useWlFinancialKpis(
  referenceMonth?: string | null,
  timezone: string = 'America/Sao_Paulo',
  statuses?: string[] | null,
) {
  // Normaliza para array estavel na queryKey.
  const statusKey = statuses && statuses.length > 0 ? [...statuses].sort().join(',') : 'default';

  return useQuery({
    queryKey: ['wl-financial-kpis', referenceMonth ?? 'current', timezone, statusKey],
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<WlFinancialKpisPayload> => {
      const { data, error } = await supabase.rpc('wl_get_financial_kpis', {
        p_reference_month: referenceMonth ?? null,
        p_timezone: timezone,
        // Passa apenas se o consumidor forneceu; NULL preserva default backend.
        p_statuses: statuses && statuses.length > 0 ? statuses : null,
      } as never);
      if (error) throw error;
      return data as unknown as WlFinancialKpisPayload;
    },
  });
}
